import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Scale, Shield, Mail, MapPin, Building } from "lucide-react";

const MentionsLegalesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="mentions-legales-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo">SophIA</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-8 max-w-4xl mx-auto page-enter">
        <h1 className="text-3xl font-medium text-[#1A365D] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          Mentions Légales
        </h1>

        <div className="space-y-6">
          {/* Éditeur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Building className="w-5 h-5" />
                Éditeur du site
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-600">
              <p><strong>Le Pool Presse</strong></p>
              <p>Société par Actions Simplifiée (SAS)</p>
              <p>Capital social : 1 000 €</p>
              <p>SIREN : 831 276 001</p>
              <p>SIRET : 831 276 001 00011</p>
              <p>RCS Chartres</p>
              <p>Code NAF/APE : 5814Z (Édition de revues et périodiques)</p>
              <p>TVA intracommunautaire : FR16831276001</p>
              <p className="flex items-center gap-2 pt-2">
                <MapPin className="w-4 h-4" />
                9 la Farinière, 28240 Fontaine-Simon, France
              </p>
            </CardContent>
          </Card>

          {/* Direction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Scale className="w-5 h-5" />
                Direction de la publication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-600">
              <p><strong>Directeur de la publication :</strong> Jean-Baptiste Giraud</p>
              <p><strong>Développement :</strong> Jean-Baptiste Giraud</p>
              <p className="flex items-center gap-2 pt-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:jbgiraud@ciceron-ai.com" className="text-[#1A365D] hover:underline">
                  jbgiraud@ciceron-ai.com
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Hébergement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Shield className="w-5 h-5" />
                Hébergement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-600">
              <p><strong>Hébergeur :</strong> Emergent</p>
              <p>Les données sont hébergées dans l'Union Européenne.</p>
            </CardContent>
          </Card>

          {/* Propriété intellectuelle */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1A365D]">Propriété intellectuelle</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-600">
              <p>
                L'ensemble du contenu de ce site (textes, images, vidéos, logos, graphismes) 
                est la propriété exclusive de Le Pool Presse ou de ses partenaires, 
                et est protégé par les lois françaises et internationales relatives 
                à la propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication, transmission, 
                dénaturation, totale ou partielle du site ou de son contenu, par quelque 
                procédé que ce soit, et sur quelque support que ce soit est interdite 
                sans l'autorisation écrite préalable de Le Pool Presse.
              </p>
              <p>
                <strong>SophIA</strong> est une marque déposée de Le Pool Presse.
              </p>
            </CardContent>
          </Card>

          {/* RGPD */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1A365D]">Protection des données personnelles (RGPD)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-600">
              <p>
                Conformément au Règlement Général sur la Protection des Données (RGPD) 
                et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, 
                de rectification, de suppression et d'opposition aux données personnelles 
                vous concernant.
              </p>
              <p>
                <strong>Données collectées :</strong> Prénom, nom, classe, établissement, 
                interactions avec la plateforme à des fins pédagogiques.
              </p>
              <p>
                <strong>Finalité :</strong> Fourniture du service d'enseignement de la philosophie 
                assisté par intelligence artificielle.
              </p>
              <p>
                <strong>Conservation :</strong> Les données sont conservées pendant la durée 
                de l'année scolaire et supprimées à la demande de l'utilisateur ou de l'établissement.
              </p>
              <p>
                <strong>Traitement local :</strong> Les fichiers d'import (listes d'élèves) 
                sont traités localement et immédiatement supprimés après import (RGPD by design).
              </p>
              <p>
                Pour exercer vos droits, contactez : {" "}
                <a href="mailto:jbgiraud@ciceron-ai.com" className="text-[#1A365D] hover:underline">
                  jbgiraud@ciceron-ai.com
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1A365D]">Cookies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-600">
              <p>
                Ce site utilise uniquement des cookies techniques nécessaires au bon 
                fonctionnement de l'application (session utilisateur, préférences).
              </p>
              <p>
                Aucun cookie publicitaire ou de tracking n'est utilisé.
              </p>
            </CardContent>
          </Card>

          {/* Limitation de responsabilité */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1A365D]">Limitation de responsabilité</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-600">
              <p>
                <strong>SophIA</strong> est un prototype (POC/MVP) d'enseignement de la philosophie 
                assisté par intelligence artificielle. Les contenus générés par l'IA sont 
                fournis à titre pédagogique et ne sauraient se substituer à l'enseignement 
                d'un professeur qualifié.
              </p>
              <p>
                Le Pool Presse ne saurait être tenu responsable des erreurs, omissions ou 
                inexactitudes pouvant figurer dans les contenus générés par l'intelligence artificielle.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => navigate("/about")}
            className="mr-4"
          >
            Qui sommes-nous ?
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
          >
            Retour à l'accueil
          </Button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          Dernière mise à jour : Mars 2026
        </p>
      </main>
    </div>
  );
};

export default MentionsLegalesPage;
